import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * NUMA che corre: un passatempo per le attese lunghe.
 *
 * Lo stesso gioco del dinosauro quando la rete non va, con la mascotte al
 * posto del dinosauro e la cancelleria di uno studio al posto dei cactus. Si
 * salta con la barra spaziatrice o con un tocco; ci si abbassa con la freccia
 * giù, o tenendo il dito sulla metà bassa del campo.
 *
 * La difficoltà cresce, e cresce in tre modi diversi, perché la sola velocità
 * diventa presto ingiusta: il terreno accelera, gli spazi fra un ostacolo e
 * l'altro si accorciano, e dopo un po' cominciano ad arrivare i fogli volanti,
 * che non si saltano - si passa sotto. Ogni cento punti si sale di livello, e
 * il livello si vede.
 *
 * Gira dentro la pagina: nessun modello, nessuna rete, nessun costo.
 */

const LARGHEZZA = 620;
const ALTEZZA = 176;
const SUOLO = ALTEZZA - 34;
const GRAVITA = 0.62;
const SPINTA = -10.6;
const CADUTA_RAPIDA = 1.9;          // quanto pesa abbassarsi mentre si è in aria
const VELOCITA_INIZIALE = 4.6;
const VELOCITA_MASSIMA = 13;
const RECORD_SALVATO = "numa_gioco_record";

// Da quanti punti in poi compare ciascuna difficoltà.
const PUNTI_PER_LIVELLO = 100;
const VOLANTI_DA = 120;
const DOPPI_DA = 260;

// NUMA, in piedi e abbassata.
const NUMA_X = 50;
const NUMA_LARGO = 42;
const NUMA_ALTO = 48;
const NUMA_ALTO_ABBASSATA = 28;

function leggiRecord() {
  try {
    return Number(localStorage.getItem(RECORD_SALVATO)) || 0;
  } catch {
    return 0;
  }
}

function scriviRecord(punti) {
  try {
    localStorage.setItem(RECORD_SALVATO, String(punti));
  } catch {
    /* finestra anonima: il record vale per questa partita */
  }
}

/** I colori del tema, letti una volta: il gioco non conosce il bianco e il nero. */
function coloriDi(elemento) {
  const stile = getComputedStyle(elemento);
  const preso = (nome, difetto) => (stile.getPropertyValue(nome) || "").trim() || difetto;
  return {
    inchiostro: preso("--ink", "#16161A"),
    tenue: preso("--ink-subtle", "#8A8A95"),
    linea: preso("--line-strong", "#D8D5CE"),
    accento: preso("--accent", "#1F3A5F"),
    evidenza: preso("--highlight", "#6D4AFF"),
    avviso: preso("--warn", "#8A5A12"),
  };
}

/** Due rettangoli che si toccano. */
function siScontrano(a, b) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

export default function GiocoNuma({ pronto = false, onChiudi }) {
  const telaRef = useRef(null);
  const partitaRef = useRef(null);
  const [stato, setStato] = useState("pronto");   // pronto | corre | finito
  const [punteggio, setPunteggio] = useState(0);
  const [livello, setLivello] = useState(1);
  const [record, setRecord] = useState(leggiRecord);

  /** Comincia, o ricomincia da capo. */
  const via = useCallback(() => {
    const partita = partitaRef.current;
    if (!partita) return;
    partita.numa = { y: SUOLO, velocita: 0, aTerra: true, abbassata: false };
    partita.ostacoli = [];
    partita.distanza = 0;
    partita.velocita = VELOCITA_INIZIALE;
    partita.prossimo = 90;
    partita.finita = false;
    partita.livello = 1;
    setPunteggio(0);
    setLivello(1);
    setStato("corre");
  }, []);

  const salta = useCallback(() => {
    const partita = partitaRef.current;
    if (!partita) return;
    if (partita.finita) { via(); return; }
    if (partita.numa.aTerra) {
      partita.numa.velocita = SPINTA;
      partita.numa.aTerra = false;
      partita.numa.abbassata = false;
    }
  }, [via]);

  const abbassati = useCallback((giu) => {
    const partita = partitaRef.current;
    if (!partita || partita.finita) return;
    partita.numa.abbassata = giu;
    // Abbassarsi in aria vuol dire scendere prima: serve a rimediare a un
    // salto partito troppo presto, ed è metà del mestiere.
    if (giu && !partita.numa.aTerra) partita.numa.velocita += CADUTA_RAPIDA;
  }, []);

  // --- il motore -----------------------------------------------------------
  useEffect(() => {
    const tela = telaRef.current;
    if (!tela) return undefined;
    const scala = window.devicePixelRatio || 1;
    tela.width = LARGHEZZA * scala;
    tela.height = ALTEZZA * scala;
    const pennello = tela.getContext("2d");
    pennello.scale(scala, scala);

    const colori = coloriDi(tela);
    const mascotte = new Image();
    mascotte.src = "/data/numa3.png";

    const partita = {
      numa: { y: SUOLO, velocita: 0, aTerra: true, abbassata: false },
      ostacoli: [],
      distanza: 0,
      velocita: VELOCITA_INIZIALE,
      prossimo: 90,
      finita: true,
      livello: 1,
      passo: 0,
      lampo: 0,          // quanti fotogrammi mostrare ancora «livello N»
    };
    partitaRef.current = partita;

    let animazione = 0;
    let ultimoPunteggio = 0;

    const punti = () => Math.floor(partita.distanza / 12);

    const nuovoOstacolo = () => {
      const p = punti();
      const sorte = Math.random();
      // I fogli volanti arrivano solo dopo un po', e non sono mai il primo
      // ostacolo che si incontra: si imparano a conoscere da riposati.
      if (p > VOLANTI_DA && sorte < 0.3) {
        return { tipo: "volante", x: LARGHEZZA, w: 26, h: 14,
                 quota: NUMA_ALTO_ABBASSATA + 6 + Math.random() * 10 };
      }
      if (p > DOPPI_DA && sorte < 0.45) {
        return { tipo: "libri", x: LARGHEZZA, w: 40, h: 26, pezzi: 2 };
      }
      if (sorte < 0.55) return { tipo: "libri", x: LARGHEZZA, w: 16, h: 24, pezzi: 1 };
      return { tipo: "libri", x: LARGHEZZA, w: 18, h: 38, pezzi: 3 };
    };

    const disegnaLibri = (o) => {
      const tinte = [colori.accento, colori.evidenza, colori.tenue];
      const altoDiUno = o.h / o.pezzi;
      for (let i = 0; i < o.pezzi; i += 1) {
        pennello.fillStyle = tinte[i % tinte.length];
        pennello.globalAlpha = 0.9;
        pennello.fillRect(o.x, SUOLO - altoDiUno * (i + 1) + 1, o.w, altoDiUno - 2);
      }
      pennello.globalAlpha = 1;
    };

    const disegnaFoglio = (o) => {
      const y = SUOLO - o.quota - o.h;
      // Un foglio che svolazza: si inclina avanti e indietro mentre scorre.
      const inclinazione = Math.sin(partita.passo / 7 + o.x / 40) * 0.25;
      pennello.save();
      pennello.translate(o.x + o.w / 2, y + o.h / 2);
      pennello.rotate(inclinazione);
      pennello.fillStyle = colori.avviso;
      pennello.globalAlpha = 0.9;
      pennello.fillRect(-o.w / 2, -o.h / 2, o.w, o.h);
      pennello.globalAlpha = 1;
      pennello.strokeStyle = colori.linea;
      pennello.lineWidth = 1;
      pennello.beginPath();
      pennello.moveTo(-o.w / 2 + 4, -o.h / 6);
      pennello.lineTo(o.w / 2 - 4, -o.h / 6);
      pennello.moveTo(-o.w / 2 + 4, o.h / 6);
      pennello.lineTo(o.w / 2 - 6, o.h / 6);
      pennello.stroke();
      pennello.restore();
    };

    const passo = () => {
      animazione = requestAnimationFrame(passo);
      partita.passo += 1;
      pennello.clearRect(0, 0, LARGHEZZA, ALTEZZA);

      // Il terreno, con qualche trattino che scorre: senza, non si vede la corsa.
      pennello.strokeStyle = colori.linea;
      pennello.lineWidth = 1.5;
      pennello.beginPath();
      pennello.moveTo(0, SUOLO + 0.5);
      pennello.lineTo(LARGHEZZA, SUOLO + 0.5);
      pennello.stroke();
      pennello.globalAlpha = 0.7;
      for (let i = 0; i < 8; i += 1) {
        const x = (LARGHEZZA + 90 * i - (partita.distanza * 1.6) % (LARGHEZZA + 90)) % (LARGHEZZA + 90);
        pennello.beginPath();
        pennello.moveTo(x, SUOLO + 9);
        pennello.lineTo(x + 22, SUOLO + 9);
        pennello.stroke();
      }
      pennello.globalAlpha = 1;

      if (!partita.finita) {
        partita.distanza += partita.velocita;
        partita.velocita = Math.min(VELOCITA_MASSIMA, VELOCITA_INIZIALE + partita.distanza / 1400);

        const livelloOra = 1 + Math.floor(punti() / PUNTI_PER_LIVELLO);
        if (livelloOra !== partita.livello) {
          partita.livello = livelloOra;
          partita.lampo = 70;
          setLivello(livelloOra);
        }

        partita.numa.velocita += GRAVITA;
        partita.numa.y += partita.numa.velocita;
        if (partita.numa.y >= SUOLO) {
          partita.numa.y = SUOLO;
          partita.numa.velocita = 0;
          partita.numa.aTerra = true;
        }

        // Gli spazi si accorciano con il livello, ma mai sotto il minimo che
        // rende l'ostacolo ancora saltabile a quella velocità.
        partita.prossimo -= partita.velocita;
        if (partita.prossimo <= 0) {
          partita.ostacoli.push(nuovoOstacolo());
          const minimo = 150 + partita.velocita * 12;
          const gioco = Math.max(70, 260 - partita.livello * 25);
          partita.prossimo = minimo + Math.random() * gioco;
        }
        partita.ostacoli.forEach((o) => { o.x -= partita.velocita; });
        partita.ostacoli = partita.ostacoli.filter((o) => o.x + o.w > -10);

        // L'urto, con un margine di clemenza: il disegno ha i bordi morbidi.
        const alta = partita.numa.abbassata && partita.numa.aTerra
          ? NUMA_ALTO_ABBASSATA : NUMA_ALTO;
        const corpo = {
          x: NUMA_X + 8, w: NUMA_LARGO - 16,
          y: partita.numa.y - alta + 4, h: alta - 6,
        };
        for (const o of partita.ostacoli) {
          const suo = o.tipo === "volante"
            ? { x: o.x + 2, y: SUOLO - o.quota - o.h, w: o.w - 4, h: o.h }
            : { x: o.x + 2, y: SUOLO - o.h, w: o.w - 4, h: o.h };
          if (siScontrano(corpo, suo)) {
            partita.finita = true;
            const fatti = punti();
            setStato("finito");
            setPunteggio(fatti);
            setRecord((precedente) => {
              if (fatti > precedente) { scriviRecord(fatti); return fatti; }
              return precedente;
            });
          }
        }

        const fatti = punti();
        if (fatti !== ultimoPunteggio) {
          ultimoPunteggio = fatti;
          setPunteggio(fatti);
        }
      }

      partita.ostacoli.forEach((o) => (o.tipo === "volante" ? disegnaFoglio(o) : disegnaLibri(o)));

      // NUMA: in corsa ondeggia appena, in aria si inclina, abbassata si schiaccia.
      const abbassata = partita.numa.abbassata && partita.numa.aTerra;
      const alto = abbassata ? NUMA_ALTO_ABBASSATA : NUMA_ALTO;
      const largo = abbassata ? NUMA_LARGO + 8 : NUMA_LARGO;
      const y = partita.numa.y - alto;
      const ondeggio = partita.finita || !partita.numa.aTerra
        ? 0
        : Math.sin(partita.passo / 5) * 1.6;
      pennello.save();
      pennello.translate(NUMA_X + largo / 2, y + alto / 2 + ondeggio);
      pennello.rotate(partita.numa.aTerra
        ? 0
        : Math.max(-0.22, Math.min(0.22, partita.numa.velocita / 55)));
      if (mascotte.complete && mascotte.naturalWidth) {
        pennello.drawImage(mascotte, -largo / 2, -alto / 2, largo, alto);
      } else {
        pennello.fillStyle = colori.evidenza;
        pennello.fillRect(-largo / 4, -alto / 2, largo / 2, alto);
      }
      pennello.restore();

      // «Livello 3», per un attimo, al centro.
      if (partita.lampo > 0) {
        partita.lampo -= 1;
        pennello.globalAlpha = Math.min(1, partita.lampo / 30);
        pennello.fillStyle = colori.inchiostro;
        pennello.font = "600 15px ui-sans-serif, system-ui, sans-serif";
        pennello.textAlign = "center";
        pennello.fillText(`livello ${partita.livello}`, LARGHEZZA / 2, 34);
        pennello.textAlign = "left";
        pennello.globalAlpha = 1;
      }
    };

    animazione = requestAnimationFrame(passo);
    // Parte da sola: la finestra si è aperta per giocare, e il primo ostacolo
    // arriva comunque dopo qualche secondo di corsa.
    const avvio = setTimeout(() => via(), 350);
    return () => { cancelAnimationFrame(animazione); clearTimeout(avvio); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- i comandi -----------------------------------------------------------
  useEffect(() => {
    const giu = (e) => {
      if (e.code === "Space" || e.code === "ArrowUp" || e.key === " ") {
        e.preventDefault();
        salta();
      } else if (e.code === "ArrowDown") {
        e.preventDefault();
        abbassati(true);
      }
    };
    const su = (e) => {
      if (e.code === "ArrowDown") abbassati(false);
    };
    window.addEventListener("keydown", giu);
    window.addEventListener("keyup", su);
    return () => {
      window.removeEventListener("keydown", giu);
      window.removeEventListener("keyup", su);
    };
  }, [salta, abbassati]);

  /*
    Col dito: sopra si salta, sotto ci si abbassa.

    Su uno schermo senza tastiera il salto è il gesto ovvio, e per abbassarsi
    serve un secondo posto dove mettere il dito: la metà bassa del campo, che è
    anche dove si guarda quando arriva un foglio.
  */
  const tocca = (e) => {
    const riquadro = e.currentTarget.getBoundingClientRect();
    const punto = e.touches?.[0]?.clientY ?? e.clientY;
    if (punto - riquadro.top > riquadro.height * 0.62) abbassati(true);
    else salta();
  };

  /*
    Quando la revisione è pronta la finestra si toglie di mezzo - ma non a metà
    partita: interrompere una corsa per mostrare un elenco è peggio che far
    aspettare un momento.
  */
  useEffect(() => {
    if (pronto && stato !== "corre") {
      const quando = setTimeout(() => onChiudi?.(), 600);
      return () => clearTimeout(quando);
    }
    return undefined;
  }, [pronto, stato, onChiudi]);

  return (
    <div className="select-none" data-testid="gioco-numa">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="numa-meta">
          {stato === "finito"
            ? "Ci hai sbattuto. Barra spaziatrice per ricominciare."
            : "Spazio per saltare, freccia giù per abbassarti sotto i fogli."}
        </p>
        <p className="numa-meta tabular-nums">
          <span data-testid="gioco-livello">livello {livello}</span>
          {" · "}
          <span data-testid="gioco-punti">{punteggio}</span>
          {record > 0 ? ` · record ${record}` : ""}
        </p>
      </div>

      <div
        role="button"
        tabIndex={0}
        aria-label="Salta"
        onMouseDown={(e) => { e.preventDefault(); tocca(e); }}
        onMouseUp={() => abbassati(false)}
        onMouseLeave={() => abbassati(false)}
        onTouchStart={(e) => { e.preventDefault(); tocca(e); }}
        onTouchEnd={() => abbassati(false)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); salta(); } }}
        className="mt-2 w-full cursor-pointer overflow-hidden rounded-md border border-line bg-surface"
      >
        <canvas
          ref={telaRef}
          data-testid="gioco-tela"
          style={{ width: "100%", height: "auto", display: "block", touchAction: "none" }}
        />
      </div>

      {stato !== "corre" && (
        <button
          type="button"
          data-testid="gioco-via"
          onClick={via}
          className="mt-2 text-[12.5px] font-medium text-brand hover:underline"
        >
          {stato === "finito" ? "Un'altra partita →" : "Comincia →"}
        </button>
      )}

      {pronto && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-ok/30 bg-ok/10 px-3 py-2">
          <p className="text-[13px] text-ink">La revisione è pronta.</p>
          <button
            type="button"
            data-testid="gioco-vedi"
            onClick={onChiudi}
            className="text-[12.5px] font-medium text-brand hover:underline"
          >
            Vedi i rilievi →
          </button>
        </div>
      )}
    </div>
  );
}
